import {
  getIsSpanishIslandsResidentFareAvailable,
  getIsFlexibleTicketAvailable,
  getIsFlexibleTicketPreSelectedAtMeta,
  getIsSeatMapAvailable,
  getIsCfarAvailable
} from "../../store/selectors/checkoutSteps";
import { useSelector } from "react-redux";
import { useMemo } from "react";
import useIsBrandedFare from "../../branded-fares/hooks/useIsBrandedFare";
import { getAncillaries, getCanSkipExtrasPage } from "../../store/flightDetails/selectors";
import { useFlightsWebMdotPaxBeforeCp } from "../../utils/experiments/funnel/flights_web_mdot_pax_before_cp";
import useUserAgent from "../useUserAgent";
import { RouteName } from "@flights/constants";
import useExtraProductsUtils from "../../ancillaries/hooks/useExtraProductsUtils";
import flights_apex_web_skip_empty_ancillary_page from "../../utils/experiments/apex/flights_apex_web_skip_empty_ancillary_page";

type Step = {
  step: Extract<
    RouteName,
    | "checkout-start"
    | "checkout-fare"
    | "checkout-ticket-type"
    | "checkout-pax"
    | "checkout-extras"
    | "checkout-seat-selection"
    | "checkout-sirf"
    | "checkout-payment"
  >;
  when: () => boolean;
  requiresAncillaries?: boolean;
  requiresOrder?: boolean;
};

const useAvailableCheckoutSteps = () => {
  const { isMobile } = useUserAgent();
  const isBrandedFare = useIsBrandedFare();
  const isSpanishIslandsResidentFareAvailable = useSelector(getIsSpanishIslandsResidentFareAvailable);
  const isFlexibleTicketPreSelectedAtMeta = useSelector(getIsFlexibleTicketPreSelectedAtMeta);
  const isCfarAvailable = useSelector(getIsCfarAvailable);
  const isFlexibleTicketAvailable = useSelector(getIsFlexibleTicketAvailable);
  const isSeatMapAvailable = useSelector(getIsSeatMapAvailable);
  const skipExtrasPage = useSelector(getCanSkipExtrasPage);
  const areTicketTypesAvailable = isFlexibleTicketAvailable || isCfarAvailable;
  const showTicketTypePage = areTicketTypesAvailable && !isBrandedFare && !isFlexibleTicketPreSelectedAtMeta;
  const showBrandedFarePage = isBrandedFare && !isFlexibleTicketPreSelectedAtMeta;
  const ancillaries = useSelector(getAncillaries);
  let showExtrasPage = ancillaries && !!skipExtrasPage ? Object.keys(ancillaries).length > 0 : true;
  const { isAnyExtras } = useExtraProductsUtils();

  if (flights_apex_web_skip_empty_ancillary_page.trackWithDefaultStage()) {
    showExtrasPage = !skipExtrasPage && isAnyExtras;
  }
  if (!isAnyExtras) {
    flights_apex_web_skip_empty_ancillary_page.stages.decide_steps_no_ancillary();
    if (isMobile) {
      flights_apex_web_skip_empty_ancillary_page.stages.decide_steps_no_ancillary_mdot();
    } else {
      flights_apex_web_skip_empty_ancillary_page.stages.decide_steps_no_ancillary_ddot();
    }
  }
  const { isVariant } = useFlightsWebMdotPaxBeforeCp();

  const steps: Array<Step> = useMemo(() => {
    const stepsDesktop = [
      { step: "checkout-pax", when: () => true },
      { step: "checkout-ticket-type", when: () => showTicketTypePage },
      { step: "checkout-extras", when: () => showExtrasPage },
      { step: "checkout-seat-selection", when: () => isSeatMapAvailable }
    ];

    const stepsMdot = [
      { step: "checkout-ticket-type", when: () => showTicketTypePage },
      ...(isVariant() ? [] : [{ step: "checkout-pax", when: () => true }]),
      { step: "checkout-extras", when: () => showExtrasPage },
      { step: "checkout-seat-selection", when: () => isSeatMapAvailable },
      ...(isVariant() ? [{ step: "checkout-pax", when: () => true }] : [])
    ];

    return [
      { step: "checkout-start", when: () => true },
      { step: "checkout-fare", when: () => showBrandedFarePage },

      ...((isMobile ? stepsMdot : stepsDesktop) as Step[]),

      {
        step: "checkout-sirf",
        when: () => isSpanishIslandsResidentFareAvailable,
        requiresAncillaries: true
      },
      {
        step: "checkout-payment",
        when: () => true,
        requiresAncillaries: true,
        requiresOrder: true
      }
    ];
  }, [
    isVariant,
    isMobile,
    isSeatMapAvailable,
    isSpanishIslandsResidentFareAvailable,
    showBrandedFarePage,
    showExtrasPage,
    showTicketTypePage
  ]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const activeSteps = useMemo(() => steps.filter(({ when }) => when()).map(({ when, ...rest }) => rest), [steps]);

  return activeSteps;
};

export default useAvailableCheckoutSteps;
